<script setup lang="ts">
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  XCircleIcon,
} from '@heroicons/vue/24/outline'
import { storeToRefs } from 'pinia'
import { watch } from 'vue'

import { TEST_IDS_CREATE } from '@/_testIds/testIdsCreate'
import {
  type SimpleNotificationLink,
  type SimpleNotificationLinkAction,
  useSimpleNotificationStore,
} from '@/stores/simpleNotificationStore'
import { translationFunctionCommon } from '@/utilities/translations'

const tfCommon = translationFunctionCommon()

// A message segment is either plain text or a clickable link (when `action` is set).
interface MessageSegment {
  text: string
  action?: SimpleNotificationLinkAction
}

// Split the message text into plain-text and link segments so the link labels can be
// rendered as clickable buttons inline within the message. Only the first occurrence of
// each label is linked.
const buildMessageSegments = (
  message: string,
  links?: SimpleNotificationLink[],
): MessageSegment[] => {
  const matches = (links ?? [])
    .map((link) => ({ action: link.action, index: message.indexOf(link.label), label: link.label }))
    .filter((match) => match.index !== -1)
    .sort((first, second) => first.index - second.index)

  if (matches.length === 0) return [{ text: message }]

  const segments: MessageSegment[] = []
  let cursor = 0
  for (const match of matches) {
    // Skip labels that overlap with an already-consumed segment.
    if (match.index >= cursor) {
      if (match.index > cursor) segments.push({ text: message.slice(cursor, match.index) })
      segments.push({ action: match.action, text: match.label })
      cursor = match.index + match.label.length
    }
  }
  if (cursor < message.length) segments.push({ text: message.slice(cursor) })

  return segments
}

const simpleNotificationStore = useSimpleNotificationStore()
const { displayDurationInMilliseconds } = storeToRefs(simpleNotificationStore)
const millisecInSecond = 1000
const durationString = `${(displayDurationInMilliseconds.value / millisecInSecond).toString()}s`

watch(simpleNotificationStore.messages, () => {
  console.debug('simpleNotificationStore.messages', simpleNotificationStore.messages)
})
</script>

<template>
  <Teleport to="body">
    <div
      class="fixed bottom-0 left-1/2 z-[10002] mb-11 flex -translate-x-1/2 flex-col items-center gap-4"
    >
      <!--
        <Transition
        v-for="message in simpleNotificationStore.messages"
        :key="message.key"
        appear
        enter-active-class="transition ease-out duration-300"
        enter-from-class="translate-y-full opacity-0"
        enter-to-class="translate-y-0 opacity-100"
        leave-active-class="transition ease-in duration-300"
        leave-from-class="translate-y-0 opacity-100"
        leave-to-class="translate-x-full opacity-0"
        :show="!!message"
        >
      -->

      <div v-for="message in simpleNotificationStore.messages" :key="message.key" class="">
        <div
          class="inline-flex flex-col items-start gap-2 rounded-lg px-5 py-2.5 shadow-md"
          :class="[
            !message.dismissable ? 'animated-notification' : '',
            {
              'bg-radical_red-100 text-radical_red-700':
                message.type === 'SimpleNotificationDanger',
              'bg-seagull-100 text-lochmara-700': message.type === 'SimpleNotificationInformation',
              'bg-keppel-100 text-keppel-800': message.type === 'SimpleNotificationSuccess',
              'bg-broom-100 text-lightning_yellow-700':
                message.type === 'SimpleNotificationWarning',
            },
          ]"
          :data-testid="TEST_IDS_CREATE.simpleNotification.root"
          :data-type="message.type"
        >
          <div class="flex items-start gap-2.5">
            <CheckCircleIcon
              v-if="message.type === 'SimpleNotificationSuccess'"
              class="h-6 w-6 text-keppel-800"
            />
            <ExclamationTriangleIcon
              v-else-if="message.type === 'SimpleNotificationWarning'"
              class="h-6 w-6 text-lightning_yellow-700"
            />
            <XCircleIcon
              v-else-if="message.type === 'SimpleNotificationDanger'"
              class="h-6 w-6 text-radical_red-700"
            />
            <InformationCircleIcon v-else class="h-6 w-6 text-lochmara-700" />
            <div class="flex-1 text-base">
              <div v-if="message.message !== undefined">
                <template
                  v-for="(segment, index) in buildMessageSegments(message.message, message.links)"
                  :key="index"
                  ><button
                    v-if="segment.action"
                    class="underline underline-offset-2 transition-colors hover:text-lochmara-900"
                    :data-testid="TEST_IDS_CREATE.simpleNotification.link"
                    type="button"
                    @click="segment.action"
                  >
                    {{ segment.text }}</button
                  ><template v-else>{{ segment.text }}</template></template
                >
              </div>
              <div v-else-if="message.type === 'SimpleNotificationSuccess'">
                {{ tfCommon('simpleNotification.defaultSuccess') }}
              </div>
              <div v-else-if="message.type === 'SimpleNotificationDanger'">
                {{ tfCommon('simpleNotification.defaultDanger') }}
              </div>
              <div v-else-if="message.type === 'SimpleNotificationWarning'">
                {{ tfCommon('simpleNotification.defaultWarning') }}
              </div>
              <div v-else>
                {{ tfCommon('simpleNotification.defaultInformation') }}
              </div>
            </div>
            <button
              v-if="message.dismissable"
              class="ml-2 text-lg leading-none"
              type="button"
              @click="() => simpleNotificationStore.close(message.key)"
            >
              {{ tfCommon('simpleNotification.close') }}
            </button>
          </div>
        </div>
      </div>
      <!-- </Transition> -->
    </div>
  </Teleport>
</template>

<style scoped>
.animated-notification {
  --duration: v-bind(durationString);
  animation: notificationAnimation var(--duration, 3s) ease-in-out forwards;
}

@keyframes notificationAnimation {
  0% {
    transform: translate(0, 100%);
    opacity: 0;
  }
  10% {
    transform: translate(0, 0);
    opacity: 1;
  }
  90% {
    transform: translate(0, 0);
    opacity: 1;
  }
  100% {
    transform: translate(100%, 0);
    opacity: 0;
  }
}
</style>
